import gql from 'graphql-tag';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import { GetCreationByIdQuery } from '../../graphql/generated/graphql';

class CreationWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getById = async (id: string): Promise<NonNullable<GetCreationByIdQuery['creation']>> => {
    try {
      const result: GetCreationByIdQuery = await this.graphqlClient.query(
        gql`
          query getCreationById($id: ID!) {
            creation(id: $id) {
              id
              assignmentId
              state
              proposed
              projectId
              createdAt
              stateUpdatedAt
              assignee {
                id
                name
                profileImageUrl
              }
              deliverables {
                id
                state
                createdAt
                stateUpdatedAt
                intent
                creative {
                  id
                  file {
                    id
                    originalFileName
                    url
                    size
                    mimeType
                    fastStart {
                      enabled
                    }
                    attribute {
                      aspectRatio {
                        size {
                          width
                          height
                        }
                      }
                      playbackTime {
                        seconds
                      }
                      volume {
                        maxVolume
                      }
                    }
                  }
                }
                attachedThumbnail {
                  id
                  file {
                    id
                    originalFileName
                    url
                    mimeType
                    size
                    attribute {
                      aspectRatio {
                        size {
                          width
                          height
                        }
                      }
                      playbackTime {
                        seconds
                      }
                    }
                  }
                }
              }
              inspectionModificationRequests {
                id
                targetDeliverableId
                items {
                  type {
                    id
                    label
                  }
                  body
                }
                createdAt
              }
              modificationRequests {
                id
                bucketId
                state
                targetDeliverableId
                feeType
                createdAt
                submittedAt
                deliveryDue
                touchedAt
                feedback {
                  id
                  body
                  createdAt
                }
                sender {
                  id
                  name
                  profileImageUrl
                }
                items {
                  id
                  type
                  typeName
                  assetIds
                  body
                  metadata
                  sender {
                    id
                    name
                    profileImageUrl
                  }
                  issue {
                    id
                    name
                    resolved
                    comments {
                      id
                      body
                      assetIds
                      createdAt
                      user {
                        ... on UserDirector {
                          id
                          name
                          email
                          profileImageUrl
                        }
                        ... on UserOptimizer {
                          id
                          name
                          email
                          profileImageUrl
                        }
                      }
                    }
                  }
                }
                deliverables {
                  id
                  createdAt
                  creative {
                    id
                    file {
                      id
                      originalFileName
                      url
                      size
                      mimeType
                      fastStart {
                        enabled
                      }
                      attribute {
                        aspectRatio {
                          size {
                            width
                            height
                          }
                        }
                        playbackTime {
                          seconds
                        }
                        volume {
                          maxVolume
                        }
                      }
                    }
                  }
                  attachedThumbnail {
                    id
                    file {
                      id
                      originalFileName
                      url
                      mimeType
                      size
                      attribute {
                        aspectRatio {
                          size {
                            width
                            height
                          }
                        }
                        playbackTime {
                          seconds
                        }
                      }
                    }
                  }
                  intent
                }
                offerPricingDetail {
                  basicPricings {
                    itemType
                    actionType
                    itemGroupName
                    itemGroupKey
                    amount
                    point
                    rewardAmount
                    rewardCurrency
                  }
                  optionPricings {
                    itemType
                    actionType
                    itemGroupName
                    itemGroupKey
                    amount
                    point
                    rewardAmount
                    rewardCurrency
                  }
                }
              }
              creativeFormat {
                id
                network {
                  id
                  key
                  name
                }
                placement {
                  id
                  key
                  name
                }
                creativeType {
                  id
                  key
                  name
                }
                attribute {
                  aspectRatio {
                    supportingAspectRatioId
                    requiredOption {
                      name
                      amount
                      points {
                        key
                        point
                        rewardAmount
                        rewardCurrency
                      }
                    }
                    aspectRatio {
                      width
                      height
                    }
                    size {
                      width
                      height
                    }
                  }
                  playbackTime {
                    supportingPlaybackTimeId
                    requiredOption {
                      name
                      amount
                      points {
                        key
                        point
                        rewardAmount
                        rewardCurrency
                      }
                    }
                    seconds
                  }
                  fastStart {
                    enabled
                  }
                }
                resizeBaseId
                selectedPricingMerchandiseOptions {
                  name
                  displayToOptimizer
                  helpUrlForOptimizer
                }
              }
              achievementId
              creationSetId
            }
          }
        `,
        { id },
      );

      const creation = result.creation as NonNullable<GetCreationByIdQuery['creation']>;
      if (creation) {
        return creation;
      } else {
        throw new Error('result.creationSet is blank');
      }
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'KaizenAd::Exceptions::Optimizer::Creation::NotFound') {
        throw new CreationNotFound({ id });
      }
      throw err;
    }
  };
}
export class CreationNotFound extends Error {
  public readonly name = 'CreationNotFound';
  public readonly message: string;
  public readonly id: string;

  constructor({ id }: { id: string }) {
    super();

    this.id = id;
    this.message = `The creation (id = ${id}) is not found.`;
  }
}

export default CreationWebApi;
